import type { AxiosInstance } from "axios";

import type {
  AssignmentDraft,
  Tag,
  TagAssignment,
  TagFormInput,
  UserAssignment,
  UserAssignmentDraft,
} from "../types";
import {
  createTag as apiCreateTag,
  updateTag as apiUpdateTag,
  deleteTag as apiDeleteTag,
  updateTagResources as apiUpdateTagResources,
  type TagsApiMountPoint,
  type TagsApiModule,
  getTagsList,
  getTagById,
  getTagResourcesList,
  type TagApiRow,
  type TaggedResourceApiRow,
} from "./request";
import {
  resolveServiceTypeFromCatalog,
  resolveTagsCatalogListParams,
} from "./productAdapters";
import { colorNameToToken, tokenToColorName } from "../colorUtils";


/**
 * In-memory tags store. Tags are global (shared across products) — what
 * varies per product is the resource catalog and labels, handled by the
 * product adapters.
 *
 * The store exposes a subscriber pattern (à la zustand-lite) so React hooks
 * can re-render when the data changes. Swapping this file for a real
 * Redux slice or RTK Query later is a one-file change — consumers stay
 * the same because they go through `useTags()` / `useTagAssignments()`.
 */

const sleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

type StoreState = {
  tags: Tag[];
  assignments: Record<string, TagAssignment[]>;
  userAssignments: Record<string, UserAssignment[]>;
  /** Resource counts from the last tag-catalog GET (authoritative when assignments are not hydrated). */
  resourceCountByTagId: Record<string, number>;
};

const subscribers = new Set<() => void>();

const state: StoreState = {
  tags: [],
  assignments: {},
  userAssignments: {},
  resourceCountByTagId: {},
};

const emit = () => {
  subscribers.forEach((cb) => cb());
};


/* ------------------------------------------------------------------ */
/* Initial data                                                        */
/* ------------------------------------------------------------------ */

// Seed tag colors use the same CSS var palette tokens as the picker
// (`PRESET_COLORS` in TagFormPage). Keeping them aligned means an edited
// seed tag re-opens with its color highlighted in the picker.
// Category objects use placeholder ids — real ids come from the backend.
const SEED_TAGS: Tag[] = [
  {
    id: "tag-production",
    name: "production",
    description: "Resources running production traffic",
    color: "var(--color-semantic-bg-tag-red)",
    category: { id: 1, name: "Environment" },
    createdAt: "2025-09-12T10:24:00Z",
    updatedAt: "2026-04-30T08:12:00Z",
  },
  {
    id: "tag-staging",
    name: "staging",
    description: "Pre-production / QA",
    color: "var(--color-semantic-bg-tag-yellow)",
    category: { id: 1, name: "Environment" },
    createdAt: "2025-09-12T10:25:00Z",
    updatedAt: "2026-04-22T16:55:00Z",
  },
  {
    id: "tag-core-network",
    name: "core-network",
    description: "Backbone & critical infra",
    color: "var(--color-semantic-bg-tag-blue)",
    category: { id: 2, name: "Team" },
    createdAt: "2025-10-01T11:00:00Z",
    updatedAt: "2026-05-02T14:01:00Z",
  },
  {
    id: "tag-noc",
    name: "noc",
    description: "Owned by NOC team",
    color: "var(--color-semantic-bg-tag-green)",
    category: { id: 2, name: "Team" },
    createdAt: "2025-10-15T09:33:00Z",
    updatedAt: "2026-04-14T10:00:00Z",
  },
  {
    id: "tag-compliance-pci",
    name: "compliance-pci",
    description: "PCI scope — restricted access",
    color: "var(--color-semantic-bg-tag-fuchsia)",
    category: { id: 3, name: "Compliance" },
    createdAt: "2025-11-04T12:00:00Z",
    updatedAt: "2026-03-29T09:14:00Z",
  },
  {
    id: "tag-experiment",
    name: "experiment",
    description: null,
    color: null,
    category: null,
    createdAt: "2026-01-10T15:20:00Z",
    updatedAt: "2026-01-10T15:20:00Z",
  },
];

const SEED_ASSIGNMENTS: Record<string, TagAssignment[]> = {
  "tag-production": [
    {
      id: "asg-1",
      tagId: "tag-production",
      serviceType: "ke-port",
      resourceId: "port-001",
      resourceLabel: "Port 001",
      createdAt: "2026-04-20T10:00:00Z",
    },
    {
      id: "asg-2",
      tagId: "tag-production",
      serviceType: "ke-bbaas",
      resourceId: "bb-002",
      resourceLabel: "Backbone 002",
      createdAt: "2026-04-21T10:00:00Z",
    },
    {
      id: "asg-3",
      tagId: "tag-production",
      serviceType: "rv2-reflector",
      resourceId: "rfl-001",
      resourceLabel: "Reflector 001",
      createdAt: "2026-04-22T10:00:00Z",
    },
  ],
  "tag-core-network": [
    {
      id: "asg-4",
      tagId: "tag-core-network",
      serviceType: "ke-bbaas",
      resourceId: "bb-001",
      resourceLabel: "Backbone 001",
      createdAt: "2026-05-01T10:00:00Z",
    },
    {
      id: "asg-5",
      tagId: "tag-core-network",
      serviceType: "ke-bbaas",
      resourceId: "bb-003",
      resourceLabel: "Backbone 003",
      createdAt: "2026-05-01T10:01:00Z",
    },
  ],
  "tag-staging": [
    {
      id: "asg-stg-1",
      tagId: "tag-staging",
      serviceType: "ke-port",
      resourceId: "port-stg-01",
      resourceLabel: "Staging port",
      createdAt: "2026-05-01T10:00:00Z",
    },
  ],
  "tag-noc": [
    {
      id: "asg-noc-1",
      tagId: "tag-noc",
      serviceType: "ke-port",
      resourceId: "port-noc-01",
      resourceLabel: "NOC watch port",
      createdAt: "2026-05-01T10:00:00Z",
    },
  ],
  "tag-compliance-pci": [
    {
      id: "asg-pci-1",
      tagId: "tag-compliance-pci",
      serviceType: "ke-cloud-connectivity",
      resourceId: "cc-pci-01",
      resourceLabel: "PCI interconnect",
      createdAt: "2026-05-01T10:00:00Z",
    },
  ],
  "tag-experiment": [
    {
      id: "asg-exp-1",
      tagId: "tag-experiment",
      serviceType: "ke-internet",
      resourceId: "inet-exp-01",
      resourceLabel: "Experiment uplink",
      createdAt: "2026-05-01T10:00:00Z",
    },
  ],
};

const SEED_USER_ASSIGNMENTS: Record<string, UserAssignment[]> = {
  "tag-production": [
    {
      id: "uasg-1",
      tagId: "tag-production",
      auth0Id: "auth0|cust-1001-user-0",
      email: "ana.garcia1001@customer-1001.example.com",
      displayName: "Ana García",
      customerId: 1001,
      customerName: "Acme Networks",
      createdAt: "2026-04-25T10:00:00Z",
    },
  ],
  "tag-core-network": [
    {
      id: "uasg-core-1",
      tagId: "tag-core-network",
      auth0Id: "auth0|cust-seed-core-1",
      email: "core.ops@seed.example.com",
      displayName: "Core Ops",
      customerId: 1002,
      customerName: "Globex Telecom",
      createdAt: "2026-04-25T10:00:00Z",
    },
  ],
  "tag-staging": [
    {
      id: "uasg-stg-1",
      tagId: "tag-staging",
      auth0Id: "auth0|cust-seed-stg-1",
      email: "qa.lead@seed.example.com",
      displayName: "QA Lead",
      customerId: 1001,
      customerName: "Acme Networks",
      createdAt: "2026-04-25T10:00:00Z",
    },
  ],
  "tag-noc": [
    {
      id: "uasg-noc-1",
      tagId: "tag-noc",
      auth0Id: "auth0|cust-seed-noc-1",
      email: "noc.team@seed.example.com",
      displayName: "NOC Team",
      customerId: 1003,
      customerName: "Initech Cloud",
      createdAt: "2026-04-25T10:00:00Z",
    },
  ],
  "tag-compliance-pci": [
    {
      id: "uasg-pci-1",
      tagId: "tag-compliance-pci",
      auth0Id: "auth0|cust-seed-pci-1",
      email: "pci.owner@seed.example.com",
      displayName: "PCI Owner",
      customerId: 1004,
      customerName: "Umbrella Connect",
      createdAt: "2026-04-25T10:00:00Z",
    },
  ],
  "tag-experiment": [
    {
      id: "uasg-exp-1",
      tagId: "tag-experiment",
      auth0Id: "auth0|cust-seed-exp-1",
      email: "lab.user@seed.example.com",
      displayName: "Lab User",
      customerId: 1001,
      customerName: "Acme Networks",
      createdAt: "2026-04-25T10:00:00Z",
    },
  ],
};

state.tags = [...SEED_TAGS];
state.assignments = JSON.parse(JSON.stringify(SEED_ASSIGNMENTS));
state.userAssignments = JSON.parse(JSON.stringify(SEED_USER_ASSIGNMENTS));

/* ------------------------------------------------------------------ */
/* Public API                                                          */
/* ------------------------------------------------------------------ */

export function subscribe(cb: () => void): () => void {
  subscribers.add(cb);
  return () => subscribers.delete(cb);
}

export function getSnapshot(): StoreState {
  return state;
}

function tagApiRowToTag(row: TagApiRow): Tag {
  return {
    id: String(row.id),
    name: row.name,
    description: row.description,
    // Convert semantic name ("red") → CSS var token for the frontend store
    color: colorNameToToken(row.color),
    category: row.category,
    createdAt: row.createdAt,
    updatedAt: row.updatedAt,
    ...(row.resourceCountByModule != null
      ? { resourceCountByModule: { ...row.resourceCountByModule } }
      : {}),
    // `tenant` powers the partner/GA multi-tenant "Customer" column in the
    // list table (mirrors the pattern used by Ports / FWaaS / etc.) and
    // `childTag` flags rows owned by a different tenant in the hierarchy
    // (informational per contract §3.1 — does NOT lock edition). Both are
    // optional on the wire, so we only forward them when present.
    ...(row.tenant != null ? { tenant: row.tenant } : {}),
    ...(row.childTag != null ? { childTag: row.childTag } : {}),
    ...(typeof row.tenantId === "number" ? { tenantId: row.tenantId } : {}),
  };
}

function aggregateResourceCountByModuleFromDrafts(
  drafts: AssignmentDraft[]
): NonNullable<Tag["resourceCountByModule"]> {
  const acc: Record<TagsApiModule, number> = {
    "kontrol-edge": 0,
    "reflector-v2": 0,
    "reflector-v1": 0,
  };
  for (const d of drafts) {
    const params = resolveTagsCatalogListParams(d.serviceType);
    if (!params) continue;
    acc[params.module] += 1;
  }
  return acc;
}

function patchTagResourceCountByModule(
  tagId: string,
  moduleCounts: NonNullable<Tag["resourceCountByModule"]>
): void {
  const tagIdx = state.tags.findIndex((t) => t.id === tagId);
  if (tagIdx < 0) return;
  const next = state.tags.slice();
  next[tagIdx] = { ...next[tagIdx], resourceCountByModule: moduleCounts };
  state.tags = next;
}

const TAG_LIST_PAGE_SIZE = 100;

let tagsCatalogInFlight:
  | { key: string; promise: Promise<void> }
  | null = null;

const tagAssignmentsInFlight = new Map<string, Promise<TagAssignment[]>>();

/**
 * Hydrates the in-memory store from `GET …/tags` (paginated). Call from
 * `useTags` / `useTagRows` with `useAxiosAuth0()` and
 * {@link TAGS_MANAGER_ACCOUNT_API_MOUNT}.
 *
 * Concurrent callers share one in-flight request (SideNav filters + list
 * page both mount `useTagRows`). The de-duplication key includes
 * `tenantId` so switching the tenant lens triggers a fresh fetch instead
 * of returning the previous tenant's snapshot.
 *
 * `tenantId` overrides the tenant derived from the JWT — required when a
 * Global Admin or Partner is acting on behalf of a child tenant. Same
 * convention used by the resource detail GETs.
 */
export async function fetchTagsCatalog(
  api: AxiosInstance,
  mount: TagsApiMountPoint,
  tenantId?: number
): Promise<void> {
  const key = `${mount}:${tenantId ?? "self"}`;
  const existing = tagsCatalogInFlight;
  // Only de-dupe when the in-flight request targets the SAME mount +
  // tenant. A different tenant lens needs a fresh round-trip — sharing
  // the previous promise would silently return the other tenant's
  // tags and overwrite the store with the wrong data.
  if (existing && existing.key === key) {
    await existing.promise;
    return;
  }

  const p = (async () => {
    const collected: TagApiRow[] = [];
    let offset = 0;
    for (;;) {
      const res = await getTagsList(api, mount, {
        limit: TAG_LIST_PAGE_SIZE,
        offset,
        tenantId,
      });
      const envelope = res.data;
      if (!envelope.success) {
        throw new Error(envelope.message || "Failed to load tags");
      }
      const page = envelope.data;
      collected.push(...page.items);
      offset += page.items.length;
      if (offset >= page.total || page.items.length === 0) {
        break;
      }
    }

    state.tags = collected.map(tagApiRowToTag);
    const counts: Record<string, number> = {};
    for (const row of collected) {
      counts[String(row.id)] = row.resourceCount;
    }
    state.resourceCountByTagId = counts;
    emit();
  })();

  tagsCatalogInFlight = { key, promise: p };
  try {
    await p;
  } finally {
    if (tagsCatalogInFlight && tagsCatalogInFlight.promise === p) {
      tagsCatalogInFlight = null;
    }
  }
}

export async function createTag(
  api: AxiosInstance,
  mount: TagsApiMountPoint,
  input: TagFormInput,
  tenantId?: number
): Promise<Tag> {
  const { data: envelope } = await apiCreateTag(
    api,
    mount,
    {
      name: input.name,
      ...(input.description !== null ? { description: input.description } : {}),
      // Convert CSS var token → semantic name for the API (e.g. "var(--color-semantic-bg-tag-red)" → "red")
      ...(input.color !== null ? { color: tokenToColorName(input.color) } : {}),
      ...(input.categoryId !== null ? { categoryId: input.categoryId } : {}),
    },
    tenantId
  );
  const row = envelope.data;
  const tag = tagApiRowToTag(row);
  state.tags = [tag, ...state.tags];
  state.resourceCountByTagId = {
    ...state.resourceCountByTagId,
    [tag.id]: row.resourceCount,
  };
  emit();
  return tag;
}

export async function updateTag(
  api: AxiosInstance,
  mount: TagsApiMountPoint,
  tagId: string,
  input: TagFormInput,
  tenantId?: number
): Promise<Tag> {
  const idx = state.tags.findIndex((t) => t.id === tagId);
  if (idx < 0) throw new Error(`Tag ${tagId} not found`);

  const { data: envelope } = await apiUpdateTag(
    api,
    mount,
    parseInt(tagId, 10),
    {
      name: input.name,
      description: input.description,
      // Convert CSS var token → semantic name for the API
      color: tokenToColorName(input.color),
      categoryId: input.categoryId,
    },
    tenantId
  );
  const updated = tagApiRowToTag(envelope.data);
  state.tags = [
    ...state.tags.slice(0, idx),
    updated,
    ...state.tags.slice(idx + 1),
  ];
  emit();
  return updated;
}

/**
 * Custom error thrown by {@link deleteTag} when the backend rejects the
 * DELETE because the tag still has rows in `TaggedResources`. Carries
 * the count returned in the 409 payload (§3.5) so the UI can surface a
 * precise "still attached to N resources" message and offer "Open tag"
 * for the user to detach.
 */
export class TagDeleteConflictError extends Error {
  readonly resourceCount: number;

  constructor(resourceCount: number, message: string) {
    super(message);
    this.name = "TagDeleteConflictError";
    this.resourceCount = resourceCount;
  }
}

interface AxiosErrorLike {
  response?: {
    status?: number;
    data?: {
      message?: string;
      data?: { resourceCount?: number };
    };
  };
}

const extractAxiosError = (err: unknown): AxiosErrorLike | null =>
  err && typeof err === "object" && "response" in err
    ? (err as AxiosErrorLike)
    : null;

/**
 * Fetch the current `resourceCount` for a tag.
 *
 * Pre-check used by the UI before showing the delete confirmation
 * (manual flow per §3.5 — the user must detach resources before delete).
 *
 * Returns the value cached from the last tag list (§3.1) when available
 * to avoid an extra round-trip; falls back to `GET /tags/:id` (§3.3)
 * otherwise. Updates the cache on success.
 *
 * Non-numeric `tagId` (in-memory seeds) → returns the local assignments
 * length so the seed tags still flow through the same UX.
 */
export async function getTagResourceCount(
  api: AxiosInstance,
  mount: TagsApiMountPoint,
  tagId: string,
  tenantId?: number
): Promise<number> {
  const cached = state.resourceCountByTagId[tagId];
  if (cached !== undefined) return cached;

  const tagIdNum = parseInt(tagId, 10);
  if (Number.isNaN(tagIdNum)) {
    return state.assignments[tagId]?.length ?? 0;
  }

  try {
    const { data: envelope } = await getTagById(api, mount, tagIdNum, tenantId);
    if (!envelope.success) {
      throw new Error(envelope.message || "Failed to load tag");
    }
    const count = envelope.data.resourceCount;
    state.resourceCountByTagId = {
      ...state.resourceCountByTagId,
      [tagId]: count,
    };
    emit();
    return count;
  } catch (err) {
    console.warn(
      "[getTagResourceCount] API failed, falling back to local assignments:",
      err
    );
    return state.assignments[tagId]?.length ?? 0;
  }
}

/**
 * Hard-delete a tag (§3.5).
 *
 * Manual flow: caller is responsible for ensuring resources are detached
 * (the wizard's Step 2 + `replaceAssignments`); this function does NOT
 * auto-detach. Backend behaviour:
 *
 *   - 200 → tag removed.
 *   - 404 → tag does not exist for this tenant.
 *   - 409 → tag still has rows in `TaggedResources`. Payload carries
 *           `data.resourceCount`. We retry the DELETE **once** after a
 *           short pause in case the 409 was a stale read; if the second
 *           attempt also 409s, we throw {@link TagDeleteConflictError}
 *           with the latest count so the UI can show "still attached to
 *           N resource(s), open the tag to detach".
 *
 * Non-numeric ids (seeds) → cleans the local store only.
 */
export async function deleteTag(
  api: AxiosInstance,
  mount: TagsApiMountPoint,
  tagId: string,
  tenantId?: number
): Promise<void> {
  const tagIdNum = parseInt(tagId, 10);

  if (!Number.isNaN(tagIdNum)) {
    const attempt = async (): Promise<AxiosErrorLike | null> => {
      try {
        await apiDeleteTag(api, mount, tagIdNum, tenantId);
        return null;
      } catch (err) {
        return extractAxiosError(err);
      }
    };

    let axErr = await attempt();
    if (axErr?.response?.status === 409) {
      // Retry once — covers transient stale reads. Same operation; we
      // do NOT auto-detach (manual flow).
      await sleep(300);
      axErr = await attempt();
    }
    if (axErr) {
      const status = axErr.response?.status;
      if (status === 409) {
        const count = axErr.response?.data?.data?.resourceCount ?? 0;
        if (count >= 0) {
          state.resourceCountByTagId = {
            ...state.resourceCountByTagId,
            [tagId]: count,
          };
          emit();
        }
        throw new TagDeleteConflictError(
          count,
          axErr.response?.data?.message ||
            `Cannot delete tag: it is still associated with ${count} resource(s). Detach them first.`
        );
      }
      if (status === 404) {
        // Tag already gone server-side — keep local store consistent.
        // Falls through to the cleanup below.
        console.warn(
          "[deleteTag] Backend returned 404 — tag already removed remotely:",
          tagId
        );
      } else {
        // TODO: remove this fallback once staging migration is applied.
        console.warn(
          "[deleteTag] API failed, falling back to in-memory:",
          axErr
        );
      }
    }
  }

  // Successful API call (or local-only id, or 404/fallback path) → clean local store.
  state.tags = state.tags.filter((t) => t.id !== tagId);
  delete state.assignments[tagId];
  delete state.userAssignments[tagId];
  const nextCounts = { ...state.resourceCountByTagId };
  delete nextCounts[tagId];
  state.resourceCountByTagId = nextCounts;
  emit();
}

const TAG_RESOURCES_PAGE_SIZE = 100;

/**
 * Hydrate Step 2 (and `useTagAssignments`) for a tag.
 *
 * Numeric tag id → pages through `GET <mount>/tags/:id/resources` (§4.1)
 * and stores the mapped `TagAssignment[]` under `state.assignments[tagId]`.
 * Non-numeric ids (seeds / temp ids during creation) or API failure →
 * falls back to whatever is already in the store so the UI keeps working.
 *
 * The API gives `(module, resourceType, resourceId, resourceLabel)`; this
 * function maps `(module, resourceType)` back to the UI `serviceType` via
 * {@link resolveServiceTypeFromCatalog}. Rows whose `(module, resourceType)`
 * pair is not in the catalog map are skipped — they'd render with no
 * picker affordance — with a console warning so we notice.
 */
export async function listAssignments(
  api: AxiosInstance,
  mount: TagsApiMountPoint,
  tagId: string,
  tenantId?: number
): Promise<TagAssignment[]> {
  // De-dup key includes the tenant override so concurrent callers acting
  // on different tenant lenses don't share a stale promise (same fix as
  // `tagsCatalogInFlight`).
  const inFlightKey = `${tagId}:${tenantId ?? "self"}`;
  const inFlight = tagAssignmentsInFlight.get(inFlightKey);
  if (inFlight) return inFlight;

  const p = listAssignmentsInner(api, mount, tagId, tenantId);
  tagAssignmentsInFlight.set(inFlightKey, p);
  try {
    return await p;
  } finally {
    if (tagAssignmentsInFlight.get(inFlightKey) === p) {
      tagAssignmentsInFlight.delete(inFlightKey);
    }
  }
}

async function listAssignmentsInner(
  api: AxiosInstance,
  mount: TagsApiMountPoint,
  tagId: string,
  tenantId?: number
): Promise<TagAssignment[]> {
  const tagIdNum = parseInt(tagId, 10);
  if (!Number.isNaN(tagIdNum)) {
    try {
      const collected: TaggedResourceApiRow[] = [];
      let offset = 0;
      for (;;) {
        const res = await getTagResourcesList(api, mount, tagIdNum, {
          limit: TAG_RESOURCES_PAGE_SIZE,
          offset,
          tenantId,
        });
        const envelope = res.data;
        if (!envelope.success) {
          throw new Error(
            envelope.message || "Failed to load tag resources"
          );
        }
        const page = envelope.data;
        collected.push(...page.items);
        offset += page.items.length;
        if (offset >= page.total || page.items.length === 0) {
          break;
        }
      }
      const next: TagAssignment[] = [];
      for (const row of collected) {
        const serviceType = resolveServiceTypeFromCatalog(
          row.module,
          row.resourceType
        );
        if (!serviceType) {
          console.warn(
            "[listAssignments] Unknown (module, resourceType) pair from API — skipping row:",
            { module: row.module, resourceType: row.resourceType, id: row.id }
          );
          continue;
        }
        next.push({
          id: String(row.id),
          tagId,
          serviceType,
          resourceId: String(row.resourceId),
          resourceLabel: row.resourceLabel ?? null,
          resourceSubtitle: row.subtitle ?? null,
          createdAt: row.createdAt,
        });
      }
      state.assignments[tagId] = next;
      state.resourceCountByTagId = {
        ...state.resourceCountByTagId,
        [tagId]: next.length,
      };
      emit();
      return [...next];
    } catch (err) {
      console.warn(
        "[listAssignments] API failed, returning current in-memory snapshot:",
        err
      );
    }
  }

  await sleep(80);
  return [...(state.assignments[tagId] ?? [])];
}

/**
 * Replace the full assignments list for a tag in a single operation.
 * Persists via `PUT /<mount>/tags/:id/resources` (full replace). The request
 * can mix `module` / `resourceType` values from any product surface; the mount
 * passed in should match the same axios client used for tag CRUD (typically
 * the active product — see contract §4.2).
 *
 * Non-numeric `tagId` (in-memory / demo) or API failure → falls back to the
 * legacy mock so local UI still updates.
 */
export async function replaceAssignments(
  api: AxiosInstance,
  mount: TagsApiMountPoint,
  tagId: string,
  drafts: AssignmentDraft[],
  tenantId?: number
): Promise<TagAssignment[]> {
  const tagIdNum = parseInt(tagId, 10);
  // Real PUT whenever the tag has a server id. An empty `drafts` array is a
  // valid full-replace per contract §4.2 — it means "detach every resource
  // currently attached" and is the exact request the user needs before the
  // §3.5 DELETE will succeed. Previously we skipped the PUT when
  // `drafts.length === 0` to avoid a no-op on the creation flow, but that
  // also silently dropped the detach-all intent in the edit flow: the local
  // store would clear, the backend would keep the pivot rows, and the next
  // DELETE would fail with 409 ("still attached to N resources"). For
  // creation with zero drafts the PUT is a harmless no-op; we accept that
  // extra round-trip to keep edit-mode detach-all correct.
  if (!Number.isNaN(tagIdNum)) {
    try {
      const resources: Array<{
        module: TaggedResourceApiRow["module"];
        resourceType: string;
        resourceId: number;
      }> = [];
      for (const d of drafts) {
        const params = resolveTagsCatalogListParams(d.serviceType);
        if (!params) {
          throw new Error(
            `Unknown service type for tags catalog: "${d.serviceType}".`
          );
        }
        const resourceId = parseInt(String(d.resourceId), 10);
        if (Number.isNaN(resourceId)) {
          throw new Error(
            `Resource id must be numeric for the API: "${d.resourceId}" (${d.serviceType}).`
          );
        }
        resources.push({
          module: params.module,
          resourceType: params.resourceType,
          resourceId,
        });
      }
      const { data: envelope } = await apiUpdateTagResources(
        api,
        mount,
        tagIdNum,
        { resources },
        tenantId
      );
      if (!envelope.success) {
        throw new Error(envelope.message || "Failed to update tag resources");
      }
      const items = envelope.data.items;
      const next: TagAssignment[] = drafts.map((d) => {
        const params = resolveTagsCatalogListParams(d.serviceType);
        const rid = parseInt(String(d.resourceId), 10);
        const row =
          params &&
          items.find(
            (r: TaggedResourceApiRow) =>
              r.module === params.module &&
              r.resourceType === params.resourceType &&
              r.resourceId === rid
          );
        return {
          id: row ? String(row.id) : `asg-${d.serviceType}-${d.resourceId}`,
          tagId,
          serviceType: d.serviceType,
          resourceId: d.resourceId,
          resourceLabel: row?.resourceLabel ?? d.resourceLabel ?? null,
          // The write response doesn't resolve subtitle (it's read-time only),
          // so fall back to the draft's value to keep it across a save.
          resourceSubtitle: row?.subtitle ?? d.resourceSubtitle ?? null,
          createdAt: row?.createdAt ?? new Date().toISOString(),
        };
      });
      state.assignments[tagId] = next;
      state.resourceCountByTagId = {
        ...state.resourceCountByTagId,
        [tagId]: next.length,
      };
      patchTagResourceCountByModule(
        tagId,
        aggregateResourceCountByModuleFromDrafts(drafts)
      );
      emit();
      return [...next];
    } catch (err) {
      console.warn(
        "[replaceAssignments] API failed, falling back to in-memory:",
        err
      );
    }
  }

  await sleep(120);
  const now = new Date().toISOString();
  const newId = (prefix: string) =>
    `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const next: TagAssignment[] = drafts.map((d) => ({
    id: newId("asg"),
    tagId,
    serviceType: d.serviceType,
    resourceId: d.resourceId,
    resourceLabel: d.resourceLabel ?? null,
    resourceSubtitle: d.resourceSubtitle ?? null,
    createdAt: now,
  }));
  state.assignments[tagId] = next;
  state.resourceCountByTagId = {
    ...state.resourceCountByTagId,
    [tagId]: next.length,
  };
  patchTagResourceCountByModule(
    tagId,
    aggregateResourceCountByModuleFromDrafts(drafts)
  );
  emit();
  return [...next];
}

/**
 * Aggregate counts used by the page's stat cards. Computed from the
 * assignments map so it stays in sync after CRUD ops.
 */
export function getAssignmentCount(tagId: string): number {
  return state.assignments[tagId]?.length ?? 0;
}

export function getAllAssignmentsByTag(): Record<string, TagAssignment[]> {
  return state.assignments;
}

/* ------------------------------------------------------------------ */
/* User assignments — tag → user grants (RBAC scaffold).               */
/*                                                                     */
/* Today these only mark "users can see resources tagged with X".     */
/* When the backend ships RBAC, this is the table that the API will   */
/* filter resource visibility against. Keep the contract narrow so     */
/* the swap to a real endpoint is a one-file change.                   */
/* ------------------------------------------------------------------ */

export async function listUserAssignments(
  tagId: string
): Promise<UserAssignment[]> {
  await sleep(80);
  return [...(state.userAssignments[tagId] ?? [])];
}

/**
 * Replace the full users list for a tag in a single operation. Mirrors
 * `replaceAssignments` (resources) so the form's single-save flow can
 * persist basic info + resources + users together.
 *
 * TODO: wire to real endpoint once the user-assignments backend contract
 * is finalised.
 */
export async function replaceUserAssignments(
  tagId: string,
  drafts: UserAssignmentDraft[]
): Promise<UserAssignment[]> {
  await sleep(120);
  const now = new Date().toISOString();
  const newId = (prefix: string) =>
    `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const next: UserAssignment[] = drafts.map((d) => ({
    id: newId("uasg"),
    tagId,
    auth0Id: d.auth0Id,
    email: d.email,
    displayName: d.displayName ?? null,
    customerId: d.customerId,
    customerName: d.customerName,
    createdAt: now,
  }));
  state.userAssignments[tagId] = next;
  emit();
  return [...next];
}

export function getUserAssignmentCount(tagId: string): number {
  return state.userAssignments[tagId]?.length ?? 0;
}

export function getAllUserAssignmentsByTag(): Record<string, UserAssignment[]> {
  return state.userAssignments;
}

/** Payload shape for optional UI/demo seeds (e.g. Notifications V2 until APIs exist). */
export type TagDemoSeedPayload = {
  tags: Tag[];
  assignments: Record<string, TagAssignment[]>;
  userAssignments: Record<string, UserAssignment[]>;
};

const mergedDemoSeedKeys = new Set<string>();

/**
 * Merges demo/API-shaped data into the in-memory store once per `seedKey`.
 * Skips tags whose `id` already exists; overwrites assignments/userAssignments for seeded tag ids.
 */
export function mergeTagDemoSeedOnce(
  seedKey: string,
  seed: TagDemoSeedPayload
): void {
  if (mergedDemoSeedKeys.has(seedKey)) return;
  mergedDemoSeedKeys.add(seedKey);

  for (const tag of seed.tags) {
    if (!state.tags.some((t) => t.id === tag.id)) {
      state.tags = [tag, ...state.tags];
    }
  }

  for (const [tagId, rows] of Object.entries(seed.assignments)) {
    state.assignments[tagId] = rows.map((r) => ({ ...r }));
    state.resourceCountByTagId = {
      ...state.resourceCountByTagId,
      [tagId]: rows.length,
    };
  }

  for (const [tagId, rows] of Object.entries(seed.userAssignments)) {
    state.userAssignments[tagId] = rows.map((r) => ({ ...r }));
  }

  emit();
}
